import React, { useCallback, useState } from 'react';
import {
  Row,
  Col,
  Divider,
  notification,
} from 'antd';
import PropTypes from 'prop-types';

import CompanySettingsCard from '../CompanySettingsCard';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import DisplayText from '../../common/text/DisplayText';
import CompanyEditRow from '../CompanyEditRow';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';

import Debouncer from '../../helpers/Debouncer';
import colors from '../../constants/Colors';
import { isNullOrUndefined } from '../../helpers/helpers';

const debouncer = new Debouncer();

export default function NightShiftPremium({
  settings,
  saveCompanySettings,
}) {
  const {
    enableNightShiftPremium,
    nightShiftPremiumMultiplier,
    nightShiftPremiumAdjustment,
  } = settings;

  const [warningShown, setWarningShown] = useState(false);

  if (!warningShown) {
    notification.warning({
      key: 'warning',
      message: 'Caution',
      description:
        'Changing night shift premium settings recalculates night shift premium retroactively, affecting all existing timecards',
      duration: 8,
    });
    setWarningShown(true);
  }

  const onToggle = useCallback(() => {
    const newValue = !enableNightShiftPremium;
    const obj = {
      enableNightShiftPremium: newValue,
    };

    if (!newValue) {
      obj.nightShiftPremiumMultiplier = null;
      obj.nightShiftPremiumAdjustment = null;
    }

    saveCompanySettings(obj);
  }, [enableNightShiftPremium, saveCompanySettings]);

  const onMultiplierChange = useCallback((isEnter) => async (e) => {
    const val = isEnter ? e.target.value : e;
    await debouncer.debounce(() => {
      saveCompanySettings({
        nightShiftPremiumMultiplier: val,
        nightShiftPremiumAdjustment: null, // Clear adjustment when multiplier is set
      });
    }, 500);
  }, [saveCompanySettings]);

  const onAdjustmentChange = useCallback((isEnter) => async (e) => {
    const val = isEnter ? e.target.value : e;
    await debouncer.debounce(() => {
      saveCompanySettings({
        nightShiftPremiumAdjustment: val,
        nightShiftPremiumMultiplier: null, // Clear multiplier when adjustment is set
      });
    }, 500);
  }, [saveCompanySettings]);

  return (
    <div
      style={{
        height: '90vh',
        overflow: 'scroll',
        width: '100%',
        paddingBottom: 50,
      }}
    >
      <CompanySettingsCard
        title="Night Shift Premium"
      >
        <Row style={{ margin: '20px 32px 20px 0px' }} justify="space-between">
          <OnTraccrSwitch
            value={enableNightShiftPremium}
            onChange={onToggle}
            title="Enable Night Shift Premium"
          />
        </Row>
        <Divider
          style={{
            backgroundColor: colors.ONTRACCR_OPACITY_GRAY,
            maxWidth: 440,
            minWidth: 440,
          }}
        />
        {enableNightShiftPremium && (
          <>
            <CompanyEditRow
              title="Night Shift Premium Multiplier"
              helpText="Multiply the base wage by this amount for night shift eligible entries. This is applied before overtime calculations."
              divider
            >
              <Row gutter={4} align="middle" style={{ marginTop: 10 }}>
                <Col>
                  <DisplayText title="wage x" style={{ marginBottom: 0 }} />
                </Col>
                <Col>
                  <OnTraccrNumberInput
                    style={{ width: 65 }}
                    formatter={(value) => Number.isNaN(Number(value)) ? 0 : value}
                    onChange={onMultiplierChange(false)}
                    onPressEnter={onMultiplierChange(true)}
                    defaultValue={nightShiftPremiumMultiplier}
                    min={1}
                    max={99}
                    step={0.25}
                    disabled={!isNullOrUndefined(nightShiftPremiumAdjustment)}
                  />
                </Col>
              </Row>
            </CompanyEditRow>
            <CompanyEditRow
              title="Night Shift Premium Adjustment"
              helpText="Add this dollar amount per hour to the base wage for night shift eligible entries. This is applied before overtime calculations."
              divider={false}
            >
              <Row gutter={4} align="middle" style={{ marginTop: 10 }}>
                <Col>
                  <DisplayText title="wage + $" style={{ marginBottom: 0 }} />
                </Col>
                <Col>
                  <OnTraccrNumberInput
                    style={{ width: 65 }}
                    formatter={(value) => Number.isNaN(Number(value)) ? 0 : value}
                    onChange={onAdjustmentChange(false)}
                    onPressEnter={onAdjustmentChange(true)}
                    defaultValue={nightShiftPremiumAdjustment}
                    min={0}
                    max={9999}
                    step={0.01}
                    disabled={!isNullOrUndefined(nightShiftPremiumMultiplier)}
                  />
                </Col>
              </Row>
            </CompanyEditRow>
          </>
        )}
      </CompanySettingsCard>
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
NightShiftPremium.propTypes = {
  settings: PropTypes.object.isRequired,
  saveCompanySettings: PropTypes.func.isRequired,
};
